/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
/*
 * @Author: Jiajia Li
 * @Date: 2024-08-26 10:03:08
 * @LastEditTime: 2024-10-01 17:15:43
 * @Description:
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "./api";
import ProductItem from "./ProductItem";
import { useParams, useSearchParams } from "react-router-dom";
import Spinner from "./Spinner";
import "./ProductList.css";
import noResultImg from "../no_result.png";

import Icons from "./Icons";

// Diagnostic tool function
const diagnoseDuplicates = (items, source = "") => {
  const nameCounts = {};
  items.forEach((item) => {
    const productName =
      item.name || item.product_name || item.title || item.product_title || "";
    const normalizedName = productName.toLowerCase().trim();
    nameCounts[normalizedName] = (nameCounts[normalizedName] || 0) + 1;
  });

  const duplicates = Object.entries(nameCounts).filter(
    ([name, count]) => count > 1
  );

  console.log(`🔍 ${source} Diagnostic Results:`, {
    totalProducts: items.length,
    uniqueProducts: Object.keys(nameCounts).length,
    duplicateProducts: duplicates.length,
    duplicateDetails: duplicates.map(
      ([name, count]) => `${name} (${count} times)`
    ),
  });

  return duplicates.length > 0;
};

// Client-side sorting function
const applySorting = (products, sortType) => {
  const sorted = [...products];
  
  switch (sortType) {
    case 'popularity':
      // Sort by review count (most popular = most reviews)
      return sorted.sort((a, b) => {
        const aReviews = a.review_count || a.rate_count || 0;
        const bReviews = b.review_count || b.rate_count || 0;
        return bReviews - aReviews;
      });
    
    case 'rating':
      // Sort by rating (highest rated first)
      return sorted.sort((a, b) => {
        const aRating = parseFloat(a.rate) || 0;
        const bRating = parseFloat(b.rate) || 0;
        return bRating - aRating;
      });
    
    default:
      return sorted;
  }
};

export const fetchProducts = async ({ request, params }) => {
  const url = new URL(request.url);
  const preferences = url.searchParams.getAll("sp");
  const skin_types = url.searchParams.getAll("st");
  const skin_concerns = url.searchParams.getAll("sc");
  const skin_sensitive = url.searchParams.getAll("ss");
  const keyword = url.searchParams.get("q");
  const sort = url.searchParams.get("sort");
  const filterParams = {
    ...(skin_types && { skin_types: skin_types }),
    ...(skin_concerns && { skin_concerns: skin_concerns }),
    ...(skin_sensitive && { skin_sensitive: skin_sensitive }),
    ...(preferences && { preferences: preferences }),
    ...(keyword && { search: keyword }),
    ...(params.categoryId && { category: params.categoryId }),
    ...(params.subCategoryId && { sub_category: params.subCategoryId }),
    ...(params.subHeadingId && { sub_heading: params.subHeadingId }),
    ...(params.brand && { brand: params.brand }),
    ...(sort && { ordering: sort }),
  };
  console.log(filterParams);
  try {
    const res = await api.get("/products", {
      params: filterParams,
    });
    let data = await res.data;
    
    // Apply client-side sorting if backend doesn't support the ordering parameter
    if (sort && data.results) {
      data.results = applySorting(data.results, sort);
    }
    
    return data;
  } catch (error) {
    throw new Response(
      "There is a temporary problem with the server, please try again later.",
      { status: 500 }
    );
  }
};

export default function ProductList({ rec_for_you = {} }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pageSize = 24;
  const [apiUrl, setApiUrl] = useState(null);

  const [searchParams] = useSearchParams();

  let { categoryId, subCategoryId, subHeadingId, brand } = useParams();

  const fetchData = async (initPage = false) => {
    setIsLoading(true);

    let preferences = searchParams.getAll("sp").join();
    let skin_types = searchParams.getAll("st").join();
    let skin_concerns = searchParams.getAll("sc").join();
    let skin_sensitive = searchParams.getAll("ss").join();
    let keyword = searchParams.get("q");
    let sort = searchParams.get("sort");
    let filterParams = {
      ...(skin_types && { skin_types: skin_types }),
      ...(skin_concerns && { skin_concerns: skin_concerns }),
      ...(skin_sensitive && { skin_sensitive: skin_sensitive }),
      ...(preferences && { preferences: preferences }),
      ...(keyword && { search: keyword }),
      ...(categoryId && { category: categoryId }),
      ...(subCategoryId && { sub_category: subCategoryId }),
      ...(subHeadingId && { sub_heading: subHeadingId }),
      ...(brand && { brand: brand }),
      ...(sort && { ordering: sort }),
    };

    let isRecForYou = false;
    if (rec_for_you && Object.keys(rec_for_you).length > 0) {
      filterParams = rec_for_you;
      isRecForYou = true;
    }

    try {
      const response =
        apiUrl && !initPage
          ? await api.get(apiUrl)
          : await api.get("/products", {
              params: {
                limit: pageSize,
                offset: 0,
                ...filterParams,
              },
            });
      const data = await response.data;
      let sortedResults = data.results;

      // Apply client-side sorting if sort parameter is provided and backend doesn't support it
      if (sort && !isRecForYou) {
        sortedResults = applySorting(sortedResults, sort);
      }

      // Debug logging to understand duplicate data
      console.log("API Response:", {
        initPage,
        resultsCount: data.results.length,
        isRecForYou,
        sort,
        apiUrl: apiUrl ? "using apiUrl" : "using /products endpoint",
      });

      // Log first few products to understand data structure
      if (data.results.length > 0) {
        console.log("Sample product data structure:", {
          firstProduct: data.results[0],
          productKeys: Object.keys(data.results[0] || {}),
          allProductNames: data.results.map((item) => ({
            id: item.id,
            name: item.name,
            product_name: item.product_name,
            title: item.title,
            product_title: item.product_title,
          })),
        });

        // Diagnose duplicate products from API (only in development mode)
        if (process.env.NODE_ENV === "development") {
          diagnoseDuplicates(data.results, "API Response Data");
        }
      }

      if (isRecForYou) {
        sortedResults = data.results.sort((a, b) => {
          const aValue = a.matching_value || 0; // Fallback to 0 if matching_value doesn't exist
          const bValue = b.matching_value || 0; // Fallback to 0 if matching_value doesn't exist
          return bValue - aValue; // Sort in descending order
        });
      }

      // Check for duplicates in API response
      const productIds = sortedResults.map((item) => item.id);
      const uniqueIds = new Set(productIds);
      if (productIds.length !== uniqueIds.size) {
        console.warn("API returned duplicate products:", {
          total: productIds.length,
          unique: uniqueIds.size,
          duplicates: productIds.length - uniqueIds.size,
        });
      }
      setApiUrl(data.next ? decodeURIComponent(data.next) : null);
      setIsLoading(false);

      // Deduplicate products by name to prevent duplicates
      setItems((prevItems) => {
        if (initPage) {
          // Deduplicate even on initial load in case API returns duplicates
          const uniqueResults = [];
          const seenNames = new Set();
          const duplicateNames = [];

          for (const item of sortedResults) {
            // Try multiple possible name fields
            const productName =
              item.name ||
              item.product_name ||
              item.title ||
              item.product_title ||
              "";
            const normalizedName = productName.toLowerCase().trim();

            if (!seenNames.has(normalizedName)) {
              seenNames.add(normalizedName);
              uniqueResults.push(item);
            } else {
              duplicateNames.push(productName);
            }
          }

          console.log("Initial load deduplication by name:", {
            original: sortedResults.length,
            unique: uniqueResults.length,
            removed: sortedResults.length - uniqueResults.length,
            duplicateNames: duplicateNames.slice(0, 10), // Show first 10 duplicates
          });
          return uniqueResults;
        } else {
          // Create a map of existing items by name for efficient lookup
          const existingItemsMap = new Map(
            prevItems.map((item) => {
              const name =
                item.name ||
                item.product_name ||
                item.title ||
                item.product_title ||
                "";
              return [name.toLowerCase().trim(), item];
            })
          );

          const newItems = sortedResults.filter((item) => {
            const productName =
              item.name ||
              item.product_name ||
              item.title ||
              item.product_title ||
              "";
            const normalizedName = productName.toLowerCase().trim();
            return !existingItemsMap.has(normalizedName);
          });

          console.log("Load more deduplication by name:", {
            existing: prevItems.length,
            newFromAPI: sortedResults.length,
            newUnique: newItems.length,
            removed: sortedResults.length - newItems.length,
          });
          return [...prevItems, ...newItems];
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setApiUrl(null);
    setItems([]); // Clear existing items when parameters change
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, categoryId, subCategoryId, subHeadingId]);

  // Handle rec_for_you changes separately to avoid infinite loops
  useEffect(() => {
    if (rec_for_you && Object.keys(rec_for_you).length > 0) {
      setApiUrl(null);
      setItems([]);
      fetchData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec_for_you]);

  // Final deduplication before rendering to ensure no duplicates
  const uniqueItems = React.useMemo(() => {
    const seenNames = new Set();
    const result = [];

    for (const item of items) {
      const productName =
        item.name ||
        item.product_name ||
        item.title ||
        item.product_title ||
        "";
      const normalizedName = productName.toLowerCase().trim();

      if (!seenNames.has(normalizedName)) {
        seenNames.add(normalizedName);
        result.push(item);
      }
    }

    // Only log in development mode to avoid performance issues
    if (process.env.NODE_ENV === "development") {
      console.log("Final render deduplication:", {
        original: items.length,
        unique: result.length,
        removed: items.length - result.length,
      });
    }

    return result;
  }, [items]);

  const getKey = useCallback((item) => {
    const idLike = item.id ?? item._id ?? item.sku ?? item.product_id;
    if (idLike != null) return String(idLike);
    const name  = (item.name || item.product_name || item.title || item.product_title || "").toLowerCase().trim();
    const brand = (item.brand || item.manufacturer || "").toLowerCase().trim();
    const size  = (item.size || "").toLowerCase().trim();
    return `${name}|${brand}|${size}`;
  }, []);

  const displayItems = useMemo(() => {
    const src = (uniqueItems && uniqueItems.length > 0) ? uniqueItems : (items || []);
    const seen = new Set();
    return src.filter((it) => {
      const k = getKey(it);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [items, uniqueItems, getKey]);

  const isInitialLoading = isLoading && displayItems.length === 0;
  const isLoadingMore    = isLoading && displayItems.length > 0;

  return (
    <div className="product-grid-view">

      {isInitialLoading && <Spinner />}
      {/* If there are no results display a message with an SVG icon*/}
      {!isLoading && displayItems.length === 0 && (
        <div className="text-center mt-5">
          <div className="empty-icon_wrapper mb-3">
            <Icons.NoResult width="75" height="75" />
          </div>
          <div className="fw-semibold fs-4 mb-2 no-result-text">
            No products found matching your search
          </div>
        </div>
      )}

      {/* If there are results, display them */}
      {!isInitialLoading && displayItems.length > 0 && (
      <div className="">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4 mt-3">
          {displayItems.map((item) => (
            <div key={getKey(item)} className="col">
              <ProductItem item={item} />
            </div>
          ))}
        </div>
      </div>
    )}

      {apiUrl && (
        <div className="text-center">
          <button className="btn btn-primary text-white m-auto px-4"
           onClick={() => fetchData(false)}
           disabled={isLoadingMore}
           aria-busy={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load More"}  
          </button>
        </div>
      )}
    </div>
  );
}
